import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useQueryClient } from '@tanstack/react-query';
import { msalInstance } from '../../../core/auth/msalInstance';
import { Workspace } from './Workspace';
import type { Strategy } from '../../../types/Strategy';
import { preloadCombineOn } from '../../../hooks/useCombineOptions';
import { preloadDataTypes } from '../../../hooks/useDataTypes';
import { preloadFormatterTypes } from '../../../hooks/useFormatterTypes';
import { preloadOperators } from '../../../hooks/useOperators';
import { preloadOverlayTypes } from '../../../hooks/useOverlayTypes';
import { useCurrentUser } from '../../../features/auth/hooks/useCurrentUser';
import { useGetDomains } from '../../../hooks/useDomains';
import { QUERY_KEY, useGetWorkspaces, useCreateWorkspace, useUpdateWorkspace, useForceUpdateWorkspace, useDeleteWorkspace } from '../../../hooks/useWorkspaces';
import { useDomainContext } from '../../../contexts/DomainContext';
import { DomainSelector } from '../domains/DomainSelector';
import { WorkspacesApiService, type ConflictPayload } from '../../../services/WorkspacesApiService';
import { GlassSkeleton } from '../common/GlassSkeleton';
import './Universe.css';

const AUTOSAVE_DEBOUNCE_MS = 1500;
const ACTIVE_WORKSPACE_SESSION_KEY = 'moneymaker-active-workspace';

type MonitorLoad = { strategyJson: string; name: string; monitorId?: number; conditionJson?: string } | null;

function ConflictDialog({
  conflict,
  onReload,
  onOverwrite,
}: {
  conflict: ConflictPayload;
  onReload: (latestState: Record<string, any>) => void;
  onOverwrite: () => void;
}) {
  return (
    <div className="conflict-dialog-container">
      <div className="conflict-dialog-card">
        <div style={{ fontSize: '1.5rem', marginBottom: '1rem', fontWeight: 700 }}>Save Conflict</div>
        <p style={{ marginBottom: '2rem', color: 'var(--text-muted)' }}>
          This workspace was updated from another device or tab.
          What would you like to do?
        </p>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
          <button
            onClick={() => onReload(conflict.latest_state_json)}
            className="header-btn"
            style={{ background: 'var(--accent-primary)', borderColor: 'transparent' }}
          >
            Reload from Cloud
          </button>
          <button
            onClick={onOverwrite}
            className="header-btn"
          >
            Overwrite
          </button>
        </div>
      </div>
    </div>
  );
}

export function Universe() {
  const user = useCurrentUser();
  const queryClient = useQueryClient();
  const { data: domains, isSuccess: domainsLoaded } = useGetDomains();
  const { activeDomainId } = useDomainContext();
  const activeDomain = domains?.find((d) => d.id === activeDomainId);
  const isSharedDomain = !!activeDomain && (
    activeDomain.domain_type === 'shared' ||
    (!activeDomain.domain_type && !activeDomain.id.startsWith('personal-'))
  );

  const preloadedAllDomainsRef = useRef(false);
  useEffect(() => {
    if (preloadedAllDomainsRef.current) return;
    if (!user?.id || !domainsLoaded || !domains || domains.length === 0) return;

    preloadedAllDomainsRef.current = true;
    const ownerId = user.id;
    const ownerEmail = user.email ?? '';

    Promise.all(
      domains
        .filter((domain) => domain.id !== activeDomainId)
        .map(async (domain) => {
          const workspaces = await WorkspacesApiService.list(ownerId, ownerEmail, domain.id);
          queryClient.setQueryData([QUERY_KEY, ownerId, domain.id], workspaces);
          for (const ws of workspaces) {
            queryClient.setQueryData([QUERY_KEY, ownerId, domain.id, ws.id], ws);
          }
        })
    ).catch((err) => {
      console.error('[Universe] Failed to preload workspaces for all domains:', err);
    });
  }, [user?.id, user?.email, domainsLoaded, domains, queryClient, activeDomainId]);

  const { data: backendWorkspaces, isSuccess: workspacesLoaded } = useGetWorkspaces();
  const activeWorkspaceSessionKey = `${ACTIVE_WORKSPACE_SESSION_KEY}:${activeDomainId ?? 'global'}`;

  const [activeTabId, setActiveTabId] = useState<string>('');
  const [workspaceStates, setWorkspaceStates] = useState<Record<string, any>>({});
  const [dirtyTabs, setDirtyTabs] = useState<Record<string, boolean>>({});
  const hydratedTabsRef = useRef<Set<string>>(new Set());
  const [, setHydrationTick] = useState(0);
  const versionMap = useRef<Record<string, number>>({});

  const markTabHydrated = useCallback((tabId: string) => {
    if (hydratedTabsRef.current.has(tabId)) return;
    hydratedTabsRef.current.add(tabId);
    setHydrationTick((v) => v + 1);
  }, []);

  useEffect(() => {
    if (!workspacesLoaded || !backendWorkspaces || backendWorkspaces.length === 0) return;
    if (!activeTabId || !backendWorkspaces.some(t => t.id === activeTabId)) {
      const persistedActiveTabId = localStorage.getItem(activeWorkspaceSessionKey);
      const persistedTab = persistedActiveTabId
        ? backendWorkspaces.find(t => t.id === persistedActiveTabId)
        : null;
      const defaultTab = persistedTab ?? backendWorkspaces.find(t => !t.is_monitor_tab) ?? backendWorkspaces[0];
      setActiveTabId(defaultTab.id);
    }
    for (const ws of backendWorkspaces) {
      if (versionMap.current[ws.id] === undefined) {
        versionMap.current[ws.id] = ws.version;
      }
    }
  }, [workspacesLoaded, backendWorkspaces, activeTabId, activeWorkspaceSessionKey]);



  useEffect(() => {
    if (!activeTabId) return;
    localStorage.setItem(activeWorkspaceSessionKey, activeTabId);
  }, [activeTabId, activeWorkspaceSessionKey]);

  useEffect(() => {
    if (!activeTabId || !backendWorkspaces || backendWorkspaces.length === 0) return;
    const activeWorkspaceData = backendWorkspaces.find((w) => w.id === activeTabId);
    if (!activeWorkspaceData) return;

    const alreadyHydrated = hydratedTabsRef.current.has(activeTabId);
    if (!alreadyHydrated) {
      setWorkspaceStates(prev => ({ ...prev, [activeTabId]: activeWorkspaceData.state_json ?? {} }));
      markTabHydrated(activeTabId);
    }
    const currentVersion = versionMap.current[activeTabId] ?? 0;
    versionMap.current[activeTabId] = Math.max(currentVersion, activeWorkspaceData.version);
  }, [backendWorkspaces, activeTabId, markTabHydrated]);

  const prevDomainIdRef = useRef<string | null>(activeDomainId);
  useEffect(() => {
    if (prevDomainIdRef.current !== activeDomainId) {
      prevDomainIdRef.current = activeDomainId;
      Object.values(pendingSaveRef.current || {}).forEach(clearTimeout);
      pendingSaveRef.current = {};
      hydratedTabsRef.current.clear();
      setWorkspaceStates({});
      setDirtyTabs({});
      versionMap.current = {};
      setActiveTabId('');
      setHydrationTick(v => v + 1);
    }
  }, [activeDomainId]);

  const createWorkspace = useCreateWorkspace();
  const updateWorkspace = useUpdateWorkspace();
  const forceUpdateWorkspace = useForceUpdateWorkspace();
  const deleteWorkspaceMutation = useDeleteWorkspace();

  const [activeConflict, setActiveConflict] = useState<(ConflictPayload & { workspaceId: string }) | null>(null);
  const pendingSaveRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const savingRefs = useRef<Record<string, boolean>>({});
  const pendingRetryRef = useRef<Record<string, { stateJson: Record<string, any> } | null>>({});

  useEffect(() => {
    return () => {
      Object.values(pendingSaveRef.current).forEach(clearTimeout);
    };
  }, []);

  const scheduleSave = useCallback((workspaceId: string, stateJson: Record<string, any>) => {
    if (!hydratedTabsRef.current.has(workspaceId)) return;
    if (pendingSaveRef.current[workspaceId]) {
      clearTimeout(pendingSaveRef.current[workspaceId]);
    }
    pendingSaveRef.current[workspaceId] = setTimeout(async () => {
      // If a save is ALREADY in flight for this specific workspace, skip this tick.
      // We store the state to be retried after the current in-flight save completes.
      if (savingRefs.current[workspaceId]) {
        pendingRetryRef.current[workspaceId] = { stateJson };
        return;
      }

      try {
        const version = versionMap.current[workspaceId];
        if (version === undefined) return;

        savingRefs.current[workspaceId] = true;
        const result = await updateWorkspace.mutateAsync({
          id: workspaceId,
          stateJson,
          version,
        });

        if ('conflict' in result && result.conflict) {
          setActiveConflict({ ...(result as ConflictPayload), workspaceId });
        } else {
          versionMap.current[workspaceId] = (result as { version: number }).version;
          setDirtyTabs((prev) => ({ ...prev, [workspaceId]: false }));
        }
      } catch (error) {
        console.error('Auto-save failed for workspace:', workspaceId, error);
      } finally {
        savingRefs.current[workspaceId] = false;
        delete pendingSaveRef.current[workspaceId];

        const retry = pendingRetryRef.current[workspaceId];
        if (retry) {
          pendingRetryRef.current[workspaceId] = null;
          scheduleSave(workspaceId, retry.stateJson);
        }
      }
    }, AUTOSAVE_DEBOUNCE_MS);
  }, [updateWorkspace]);

  const updateState = useCallback((tabId: string, newState: any) => {
    const sanitized: Record<string, any> = {
      ...newState,
      strategies: (newState?.strategies ?? []).map((s: Strategy) => {
        const { result, ...rest } = s;
        return rest;
      }),
    };
    setWorkspaceStates(prev => ({ ...prev, [tabId]: sanitized }));
    if (isSharedDomain) {
      setDirtyTabs(prev => ({ ...prev, [tabId]: true }));
      return;
    }
    scheduleSave(tabId, sanitized);
  }, [isSharedDomain, scheduleSave]);

  const reloadActiveWorkspace = useCallback(async () => {
    if (!user?.id || !activeDomainId || !activeTabId) return;
    try {
      const latest = await WorkspacesApiService.get(user.id, user.email ?? '', activeTabId, activeDomainId);
      setWorkspaceStates(prev => ({ ...prev, [activeTabId]: latest.state_json ?? {} }));
      versionMap.current[activeTabId] = latest.version;
      markTabHydrated(activeTabId);
      setDirtyTabs(prev => ({ ...prev, [activeTabId]: false }));
    } catch (error) {
      console.error('Failed to reload workspace from cloud:', error);
    }
  }, [user?.id, user?.email, activeDomainId, activeTabId, markTabHydrated]);

  const saveActiveWorkspace = useCallback(async () => {
    if (!activeTabId) return;
    const stateJson = workspaceStates[activeTabId] ?? {};
    const version = versionMap.current[activeTabId];
    if (version === undefined) return;
    try {
      const result = await updateWorkspace.mutateAsync({
        id: activeTabId,
        stateJson,
        version,
      });
      if ('conflict' in result && result.conflict) {
        setActiveConflict({ ...(result as ConflictPayload), workspaceId: activeTabId });
      } else {
        versionMap.current[activeTabId] = (result as { version: number }).version;
        setDirtyTabs(prev => ({ ...prev, [activeTabId]: false }));
      }
    } catch (error) {
      console.error('Manual save failed for workspace:', activeTabId, error);
    }
  }, [activeTabId, workspaceStates, updateWorkspace]);

  const tabs = backendWorkspaces ?? [];

  const addNewTab = async () => {
    const newId = uuidv4();
    const name = `Workspace ${tabs.filter(t => !t.is_monitor_tab).length + 1}`;
    await createWorkspace.mutateAsync({ id: newId, name, stateJson: {} });
    setWorkspaceStates((prev) => ({ ...prev, [newId]: {} }));
    setDirtyTabs((prev) => ({ ...prev, [newId]: false }));
    setActiveTabId(newId);
    versionMap.current[newId] = 1;
    markTabHydrated(newId);
  };

  const renameTab = async (id: string, name: string) => {
    const version = versionMap.current[id];
    if (version === undefined) return;
    const result = await updateWorkspace.mutateAsync({ id, name, version });
    if ('conflict' in result && result.conflict) {
      setActiveConflict({ ...result, workspaceId: id });
    } else {
      versionMap.current[id] = (result as { version: number }).version;
    }
  };

  const removeTab = async (id: string) => {
    const tab = tabs.find(t => t.id === id);
    if (!tab || tab.is_monitor_tab) return;
    await deleteWorkspaceMutation.mutateAsync({ id });
    if (id === activeTabId && tabs.length > 1) {
      const next = tabs.find(t => t.id !== id);
      if (next) setActiveTabId(next.id);
    }
    setWorkspaceStates(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setDirtyTabs(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    delete versionMap.current[id];
    hydratedTabsRef.current.delete(id);
    setHydrationTick((v) => v + 1);
    if (pendingSaveRef.current[id]) {
      clearTimeout(pendingSaveRef.current[id]);
      delete pendingSaveRef.current[id];
    }
  };

  const handleReloadFromCloud = useCallback((latestState: Record<string, any>) => {
    if (!activeConflict) return;
    setWorkspaceStates(prev => ({ ...prev, [activeConflict.workspaceId]: latestState }));
    versionMap.current[activeConflict.workspaceId] = activeConflict.latest_version;
    markTabHydrated(activeConflict.workspaceId);
    setDirtyTabs(prev => ({ ...prev, [activeConflict.workspaceId]: false }));
    setActiveConflict(null);
  }, [activeConflict, markTabHydrated]);

  const handleForceOverwrite = useCallback(async () => {
    if (!activeConflict) return;
    const stateToSave = workspaceStates[activeConflict.workspaceId];
    if (!stateToSave) return;
    const result = await forceUpdateWorkspace.mutateAsync({ id: activeConflict.workspaceId, stateJson: stateToSave });
    versionMap.current[activeConflict.workspaceId] = result.version;
    markTabHydrated(activeConflict.workspaceId);
    setDirtyTabs(prev => ({ ...prev, [activeConflict.workspaceId]: false }));
    setActiveConflict(null);
  }, [activeConflict, workspaceStates, forceUpdateWorkspace, markTabHydrated]);

  const [deepLinkStrategyId, setDeepLinkStrategyId] = useState<number | null>(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('strategyId');
    return id ? parseInt(id, 10) : null;
  });
  const [pendingMonitorLoad, setPendingMonitorLoad] = useState<MonitorLoad>(null);

  const getOrActivateMonitorTab = () => {
    const monitor = tabs.find(t => t.is_monitor_tab);
    if (!monitor) return null;
    setActiveTabId(monitor.id);
    return monitor.id;
  };

  const deepLinkCleared = useRef(false);
  useEffect(() => {
    if (deepLinkStrategyId !== null && !deepLinkCleared.current && tabs.length > 0) {
      deepLinkCleared.current = true;
      const url = new URL(window.location.href);
      url.searchParams.delete('strategyId');
      window.history.replaceState({}, '', url.toString());
      getOrActivateMonitorTab();
    }
  }, [deepLinkStrategyId, tabs]);

  const [ready, setReady] = useState(false);
  useEffect(() => {
    Promise.all([
      preloadOperators(),
      preloadDataTypes(),
      preloadFormatterTypes(),
      preloadCombineOn(),
      preloadOverlayTypes(),
    ])
      .catch(err => console.error('Failed to preload types:', err))
      .finally(() => setReady(true));
  }, []);

  const [theme, setTheme] = useState<'dark' | 'light'>(() =>
    localStorage.getItem('moneymaker-theme') as 'dark' | 'light' || 'dark'
  );
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('moneymaker-theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(prev => prev === 'dark' ? 'light' : 'dark');

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!userMenuRef.current) return;
      if (!userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const handleLogout = useCallback(() => {
    const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
    msalInstance.logoutRedirect({
      account,
      postLogoutRedirectUri: window.location.origin + '/login',
    }).catch((error) => {
      console.error('Logout failed:', error);
    });
  }, []);

  const activeTab = tabs.find(t => t.id === activeTabId);
  const activeState = workspaceStates[activeTabId] ?? {};
  const activeTabSetState = useMemo(() => (newState: any) => updateState(activeTabId, newState), [updateState, activeTabId]);

  const isLoading = !workspacesLoaded;
  useEffect(() => {
    if (isLoading) {
      console.log(`[Universe] Loading state: workspacesLoaded=${workspacesLoaded}`);
    }
  }, [isLoading, workspacesLoaded]);

  const isActiveTabHydrated = !!activeTab && hydratedTabsRef.current.has(activeTab.id);
  const isActiveTabLoading = !!activeTab && !isActiveTabHydrated;
  const isActiveSharedDirty = !!activeTabId && !!dirtyTabs[activeTabId];

  return (
    <div>
      {activeConflict && (
        <ConflictDialog
          conflict={activeConflict}
          onReload={handleReloadFromCloud}
          onOverwrite={handleForceOverwrite}
        />
      )}

      <header className="universe-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
          <div className="brand-logo">
            MoneyMaker
          </div>
          <button
            onClick={toggleTheme}
            className="theme-toggle"
            title={`Switch to ${theme === 'dark' ? 'Light' : 'Dark'} Mode`}
          >
            {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
          </button>

          {user && (
            <div ref={userMenuRef} style={{ position: 'relative', flexShrink: 0, marginLeft: '20px' }}>
              <button
                onClick={() => setIsUserMenuOpen((prev) => !prev)}
                className="user-avatar-btn"
                title={user.name}
              >
                <div className="user-initials">
                  {user.initials}
                </div>
              </button>
              {isUserMenuOpen && (
                <div className="menu-dropdown" style={{ right: 0, top: 'calc(100% + 8px)', left: 'auto' }}>
                  <button
                    onClick={handleLogout}
                    className="menu-item"
                  >
                    Sign out
                  </button>
                </div>
              )}
            </div>
          )}

          <div style={{ marginLeft: '0.6rem' }}>
            <DomainSelector />
          </div>

          {isSharedDomain && activeTabId && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <button
                onClick={reloadActiveWorkspace}
                className="header-btn"
              >
                Reload
              </button>
              <button
                onClick={saveActiveWorkspace}
                disabled={!isActiveSharedDirty}
                className="header-btn"
                style={{
                  background: isActiveSharedDirty ? 'var(--accent-primary)' : 'transparent',
                  borderColor: isActiveSharedDirty ? 'transparent' : 'var(--surface-glass-border)',
                  color: isActiveSharedDirty ? 'white' : 'var(--text-muted)',
                }}
              >
                Save
              </button>
            </div>
          )}

        </div>

        <div style={{ flex: 1 }} />

        <div className="nav-tabs-container">
          {isLoading ? (
            <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Loading workspaces...</span>
          ) : (
            <>
              {tabs.map((tab) => (
                <div
                  key={tab.id}
                  onClick={() => setActiveTabId(tab.id)}
                  onDoubleClick={() => {
                    if (tab.is_monitor_tab) return;
                    const newName = prompt('Rename workspace:', tab.name);
                    if (newName) renameTab(tab.id, newName);
                  }}
                  className={`nav-tab ${tab.id === activeTabId ? 'active' : ''} ${tab.is_monitor_tab ? 'monitor' : ''}`}
                  title={tab.name}
                >
                  <span className="tab-name">{tab.name}</span>
                  {!tab.is_monitor_tab && tabs.length > 1 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); removeTab(tab.id); }}
                      title="Close workspace"
                      className="close-ws-btn"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <div
                onClick={addNewTab}
                className="add-tab-btn"
                title="Add Workspace"
              >
                +
              </div>
            </>
          )}
        </div>
      </header>

      <div className="flex-1 overflow-hidden" style={{ background: 'var(--bg-app)', height: 'calc(100vh - var(--header-height))' }}>
        {ready && isActiveTabLoading && (
          <div style={{ padding: '2rem', display: 'flex', flexDirection: 'column', gap: '1.5rem', height: '100%', boxSizing: 'border-box' }}>
            <GlassSkeleton width="200px" height="2.5rem" />
            <div style={{ display: 'flex', gap: '1.5rem', flex: 1, overflow: 'hidden' }}>
              <GlassSkeleton width="250px" height="100%" borderRadius="8px" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', flex: 1 }}>
                <GlassSkeleton width="100%" height="40%" borderRadius="8px" />
                <GlassSkeleton width="100%" height="60%" borderRadius="8px" />
              </div>
            </div>
          </div>
        )}
        {ready && activeTab && isActiveTabHydrated && (
          <Workspace
            key={activeTab.id}
            workspaceId={activeTab.id}
            state={activeState}
            setState={activeTabSetState}
            deepLinkStrategyId={activeTab.is_monitor_tab ? deepLinkStrategyId : null}
            onDeepLinkLoaded={() => setDeepLinkStrategyId(null)}
            isMonitorTab={activeTab.is_monitor_tab ?? false}
            pendingMonitorLoad={activeTab.is_monitor_tab ? pendingMonitorLoad : null}
            onPendingMonitorLoadConsumed={() => setPendingMonitorLoad(null)}
            onLoadMonitorStrategy={(strategyJson, name, monitorId, conditionJson) => {
              const monitorTabId = getOrActivateMonitorTab();
              if (!monitorTabId) return;
              setPendingMonitorLoad({ strategyJson, name, monitorId, conditionJson });
            }}
          />
        )}
      </div>
    </div>
  );
}
